import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  useAgreementAcceptanceMutation,
  useApplicationQuery,
  useClientOfferQuery,
  useConfirmApplicationMutation,
  useSecciAcceptanceMutation,
} from 'hooks/webapi';
import useAgreementQuery from 'hooks/webapi/client/useAgreementQuery';
import {
  useSecciPDFQuery,
  useSecciQuery,
} from 'hooks/webapi/client/useSecciQuery';
import useNavigateByApplication from 'pages/Waiting/useNavigateByApplication';
import H1 from 'components/H/H1';
import { useNavigate } from 'react-router';
import { SIGN_AGREEMENT } from 'Router/routes';
import { dateFormat, numberToCurrency, numberToPercent } from 'utils/translate';
import styles from './Agreement.module.scss';
import DigitalSignature from './DigitalSignature/DigitalSignature';
import {
  Button,
  Card,
  Checkbox,
  DocumentModal,
  Placeholder,
  Typography,
} from '../../../components';

export default function Agreement() {
  const { t } = useTranslation([
    'consent',
    'common',
    'calculator',
    'digital_signature',
  ]);
  const [legalIsOpen, setLegalIsOpen] = useState(false);
  const [agreementIsOpen, setAgreementIsOpen] = useState(false);
  const [digitalSignatureIsOpen, setDigitalSignatureIsOpen] = useState(false);
  const [pending, setPending] = useState(false);

  const [legalChecked, setLegalChecked] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  const { mutateAsync: confirmClientApplication } =
    useConfirmApplicationMutation();
  const { data: application } = useApplicationQuery(true, false);
  const { data: agreementHtml } = useAgreementQuery({
    amount: application?.amount,
    term: application?.term,
    fileType: 'HTML',
  });
  const { data: agreementPdfData, refetch: refetchAgreementPdf } =
    useAgreementQuery({
      amount: application?.amount,
      term: application?.term,
      fileType: 'PDF',
      enabled: false,
    });

  const { data: secciHtml } = useSecciQuery();
  const { data: secciPdfData, refetch: refetchSecciPdf } = useSecciPDFQuery({
    enabled: false,
  });

  const { mutateAsync: acceptSECCI } = useSecciAcceptanceMutation();
  const { mutateAsync: acceptAgreement } = useAgreementAcceptanceMutation();
  const [agreementValue, setAgreementValue] = useState(false);
  const { navigateNext } = useNavigateByApplication();
  const navigate = useNavigate();
  const { data: offer } = useClientOfferQuery(
    {
      amount: application?.amount,
      term: application?.term,
    },
    !!application,
  );

  useEffect(() => {
    if (application && application.confirmed) {
      navigateNext(application);
    }
  }, [application, navigateNext]);

  const handleSubmit = useCallback(async () => {
    try {
      setDigitalSignatureIsOpen(false);
      setPending(true);

      await acceptSECCI();
      await acceptAgreement();
      await confirmClientApplication();
    } catch {
      setPending(false);
    }
  }, [acceptAgreement, acceptSECCI, confirmClientApplication]);

  const handleLegalModal = useCallback(() => {
    if (!legalChecked) setLegalIsOpen(!legalIsOpen);
    else setLegalChecked(false);
  }, [legalChecked, legalIsOpen]);

  const handleOpenAgreementModal = useCallback(() => {
    if (!agreementChecked) setAgreementIsOpen(true);
    else setAgreementChecked(false);
  }, [agreementChecked]);

  const handleCloseDigitalSignature = useCallback(() => {
    setDigitalSignatureIsOpen(false);
  }, []);

  const legalOptions = {
    header: {
      title: t('modal_agreement'),
      closeButton: true,
      banner: t('banner'),
    },
    footer: {
      acceptTitle: t('common:confirm'),
      acceptPrint: t('common:print'),
      onAccept: () => {
        setLegalChecked(true);
        setLegalIsOpen(false);
      },
      print: true,
    },
  };

  const agreementOptions = {
    header: {
      title: t('modal_legal'),
      closeButton: true,
      banner: t('banner'),
    },
    footer: {
      acceptTitle: t('common:confirm'),
      acceptPrint: t('common:print'),
      onAccept: () => {
        setAgreementChecked(true);
      },
      print: true,
    },
  };

  const digitalSignatureOptions = {
    header: {
      title: t('digital_signature:modal_digital_signature'),
      closeButton: true,
    },
  };

  useEffect(() => {
    setAgreementValue(agreementValue);
  }, [agreementValue]);

  const handleAgreementValue = useCallback(() => {
    setAgreementValue(true);
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>
      <div className={styles['box-wrapper']}>
        <H1>{t('contract_documentation')}</H1>
      </div>
      <div>
        <Card className={styles.card}>
          <div className={styles.offer}>
            <div className={styles.row}>
              <span>{t('calculator:offer.loan')}</span>
              <span data-test="new-principal">
                {numberToCurrency(offer?.newPrincipal)}
              </span>
            </div>
            <div className={styles.row}>
              <span>{t('calculator:offer.mtp')}</span>
              <span data-test="loan-extension-fees">
                {numberToCurrency(offer?.loanExtensionFees?.[0].fee)}
              </span>
            </div>
            <div className={styles.row}>
              <span>{t('calculator:offer.futureExtensionFees')}</span>
              <span data-test="new-futureExtensionFees">
                {numberToCurrency(offer?.futureExtensionFees[0].fee)}
              </span>
            </div>
            <div className={styles.row}>
              <span>{t('calculator:offer.due_date')}</span>
              <span data-test="agreement-end-date">
                {dateFormat(offer?.agreementEndDate)}
              </span>
            </div>
            <div className={styles.row}>
              <span>{t('calculator:offer.interestRate')}</span>
              <span data-test="interest-rate">
                {numberToPercent(offer?.interestRate)}
              </span>
            </div>
          </div>
          <Checkbox
            name="legal"
            className={styles['check-box']}
            onChange={handleLegalModal}
            value={legalChecked}
            disabled={!secciHtml}
            dataTest="agreement-consents-checkbox"
          >
            <Typography size="s">
              <Placeholder loading={!secciHtml} dataTest="legal" replace>
                <span
                  className={styles.highlight}
                  data-test="agreement-consents-checkbox-text"
                  dangerouslySetInnerHTML={{
                    __html: t('read'),
                  }}
                />
              </Placeholder>
            </Typography>
          </Checkbox>

          <Checkbox
            name="agreement"
            onChange={handleOpenAgreementModal}
            className={styles['check-box']}
            value={agreementChecked}
            disabled={!agreementHtml}
            dataTest="agreement-legal-checkbox"
          >
            <Typography size="s">
              <Placeholder
                loading={!agreementHtml}
                dataTest="agreement-legal-checkbox-placeholder"
                placeholder=""
                replace
              >
                <span
                  className={styles.highlight}
                  dangerouslySetInnerHTML={{
                    __html: t('comfirm'),
                  }}
                  data-test="agreement-legal-checkbox-text"
                />
              </Placeholder>
            </Typography>
          </Checkbox>
        </Card>
        <div className={styles['box-wrapper']}>
          <Button
            onClick={() => navigate(SIGN_AGREEMENT)}
            pending={pending}
            disabled={!legalChecked || !agreementChecked}
            dataTest="submit_btn_agreement"
          >
            {t('common:continue')}
          </Button>
          {legalIsOpen && !!secciHtml && (
            <DocumentModal
              dataTest="legal-modal"
              onClose={() => setLegalIsOpen(false)}
              options={legalOptions}
              className={styles.modal}
              classNameModalContent={styles['modal-content']}
              classNameOverlay={styles['modal-overlay']}
              fetchPdf={refetchSecciPdf}
              pdfData={!isNil(secciPdfData) ? secciPdfData : undefined}
            >
              <div
                className={styles['secci-overrides']}
                data-test="agreement-modal-legal-text"
                dangerouslySetInnerHTML={{
                  __html: secciHtml,
                }}
              />
            </DocumentModal>
          )}
          {agreementIsOpen && (
            <DocumentModal
              dataTest="agreement-modal"
              onClose={() => setAgreementIsOpen(false)}
              options={agreementOptions}
              className={styles.modal}
              classNameModalContent={styles['modal-content']}
              classNameOverlay={styles['modal-overlay']}
              fetchPdf={refetchAgreementPdf}
              pdfData={
                !isNil(agreementPdfData) && agreementPdfData instanceof Blob
                  ? agreementPdfData
                  : undefined
              }
            >
              <div
                className={styles['agreement-overrides']}
                data-test="agreement-modal-text"
                dangerouslySetInnerHTML={{
                  __html: agreementHtml as string,
                }}
              />
            </DocumentModal>
          )}
        </div>
      </div>
      {digitalSignatureIsOpen && (
        <DocumentModal
          dataTest="digital-signature-modal"
          onClose={handleCloseDigitalSignature}
          options={digitalSignatureOptions}
          className={styles.modal}
          classNameModalContent={styles['modal-content']}
          classNameOverlay={styles['modal-overlay']}
          containerClassName={styles['modal-digital-signature']}
        >
          <DigitalSignature
            handleAgreementValue={handleAgreementValue}
            handleCloseDigitalSignature={handleSubmit}
          />
        </DocumentModal>
      )}
    </>
  );
}
